import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AuditLog.css";
import api from "../../services/api";
import { getSocket } from "../../services/socket";
import { useToast } from "../../Contexts/ToastContext/ToastContext";
import { classifyType, EXPIRY_EVENT_TYPES } from "./eventTypes";

/*
 * AuditLog — the "Log" page.
 *
 * Every mutation in the backend funnels through `logAudit(req, {...})`, so
 * this page is the single window into user activity. The UI is built around
 * fast narrowing:
 *   - top search bar runs a server-side full-text query against user,
 *     description, type, thingId, and common details fields
 *   - chip-style multi-select for event types, thing types, and roles,
 *     populated from /audit-events/distinct
 *   - date range, sortable column headers, expandable rows for full JSON
 *   - "Seed logs" button populates ~80 realistic entries for demos/testing
 *   - "Clear log" wipes the collection (still records its own clear event)
 *
 * No external chart or date libs — keep dependencies stable.
 */

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

// Classification + expiry-event list live in ./eventTypes so the dedicated
// Expiry Log page can share the same metadata.

// Human-friendly time formatter with relative fallback ("3 min ago").
const formatWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const diff = Date.now() - d.getTime();
  const abs = Math.abs(diff);
  const sign = diff >= 0 ? "ago" : "from now";
  if (abs < 45 * 1000)        return `just now`;
  if (abs < 60 * 60 * 1000)   return `${Math.round(abs / 60000)} min ${sign}`;
  if (abs < 24 * 60 * 60 * 1000) return `${Math.round(abs / 3600000)} h ${sign}`;
  if (abs < 7 * 24 * 60 * 60 * 1000) return `${Math.round(abs / 86400000)} d ${sign}`;
  return d.toLocaleString();
};
const formatExact = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString();
};

// --- small chip component --------------------------------------------------

const Chip = ({ active, onClick, label, count, family, icon }) => (
  <button
    type="button"
    className={`log-chip log-chip-${family || "default"}${active ? " is-active" : ""}`}
    onClick={onClick}
    title={active ? "Click to remove" : "Click to filter"}
  >
    {icon && <i className={`fa ${icon}`} aria-hidden />}
    <span>{label}</span>
    {typeof count === "number" && <span className="log-chip-count">{count}</span>}
  </button>
);

// ---------------------------------------------------------------------------

const AuditLog = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();

  // Rows for tag events are clickable → /tags/:tid. Non-tag events stay
  // inert so we don't pretend they do something.
  const tidTarget = (evt) => {
    if (!evt) return null;
    if (evt.thingType !== "tag") return null;
    const tid = evt.thingId || evt.details?.tid;
    return tid ? `/tags/${encodeURIComponent(tid)}` : null;
  };

  // Data
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [distincts, setDistincts] = useState({ types: [], users: [], thingTypes: [], roles: [] });
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [clearing, setClearing] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Filters
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedTypes, setSelectedTypes] = useState([]);         // string[]
  const [selectedThingTypes, setSelectedThingTypes] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [userFilter, setUserFilter] = useState("");
  const [tidFilter, setTidFilter] = useState("");
  const [fromFilter, setFromFilter] = useState("");
  const [toFilter, setToFilter] = useState("");

  // Sort
  const [sortBy, setSortBy] = useState("timestamp");
  const [sortDir, setSortDir] = useState("desc");

  // Show/hide Clear-log confirm. The clear action requires re-entering a
  // server-side password (env var AUDIT_CLEAR_PASSWORD) so we also track
  // the entered value here; it's cleared whenever the modal opens/closes.
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [clearPassword, setClearPassword] = useState("");
  // This field is a raw <input> rather than <TextField>, so it doesn't
  // inherit the shared reveal toggle — it gets its own.
  const [showClearPassword, setShowClearPassword] = useState(false);

  const totalPages = useMemo(
    () => (total > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1),
    [total, pageSize]
  );

  // Debounce the free-text search so we don't hammer the API per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  const fetchEvents = useCallback(async (nextPage = page) => {
    setLoading(true);
    try {
      const data = await api.getAuditEvents({
        page: nextPage,
        limit: pageSize,
        q: debouncedSearch,
        type: selectedTypes,
        // Expiry events are owned by the dedicated Expiry Log page.
        excludeTypes: EXPIRY_EVENT_TYPES,
        thingType: selectedThingTypes,
        role: selectedRoles,
        user: userFilter.trim(),
        tid: tidFilter.trim(),
        from: fromFilter,
        to: toFilter,
        sortBy,
        sortDir,
      });
      setEvents(data.events || []);
      setTotal(data.total || 0);
      setPage(data.page || nextPage);
    } catch (err) {
      showToast(err.message || "Failed to load audit log", 3000, true, "danger");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, selectedTypes, selectedThingTypes, selectedRoles,
      userFilter, tidFilter, fromFilter, toFilter, sortBy, sortDir, showToast]);

  const fetchDistincts = useCallback(async () => {
    try {
      const d = await api.getAuditDistincts();
      setDistincts(d || { types: [], users: [], thingTypes: [], roles: [] });
    } catch {
      /* non-fatal — the chip lists will be empty */
    }
  }, []);

  // Reset to page 1 whenever filters change, then fetch.
  useEffect(() => {
    setPage(1);
    fetchEvents(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, selectedTypes, selectedThingTypes, selectedRoles,
      userFilter, tidFilter, fromFilter, toFilter, sortBy, sortDir, pageSize]);

  useEffect(() => { fetchDistincts(); }, [fetchDistincts]);

  // Real-time updates: the backend emits `audit:new` the moment an audit
  // event is persisted (whether from a Node controller or forwarded from
  // the Python reader backend after a gate detection).  When we're on the
  // first page we re-fetch so the new row appears at the top with the
  // current filters applied — doing a full refetch is simpler and more
  // correct than trying to mirror the server's filter logic client-side.
  // On deeper pages we leave pagination alone; the event will surface
  // when the user navigates back.
  useEffect(() => {
    let socket;
    try { socket = getSocket(); } catch { return undefined; }
    if (!socket) return undefined;
    const onNew = () => {
      if (page === 1) fetchEvents(1);
    };
    socket.on("audit:new", onNew);
    return () => socket.off("audit:new", onNew);
  }, [page, fetchEvents]);

  // ----- filter helpers ----
  const toggleMulti = (setter) => (value) => {
    setter((prev) => prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value]);
  };

  const clearFilters = () => {
    setSearch("");
    setSelectedTypes([]);
    setSelectedThingTypes([]);
    setSelectedRoles([]);
    setUserFilter("");
    setTidFilter("");
    setFromFilter("");
    setToFilter("");
    setSortBy("timestamp");
    setSortDir("desc");
  };

  const activeFilterCount =
    (search.trim() ? 1 : 0) +
    selectedTypes.length +
    selectedThingTypes.length +
    selectedRoles.length +
    (userFilter.trim() ? 1 : 0) +
    (tidFilter.trim() ? 1 : 0) +
    (fromFilter ? 1 : 0) +
    (toFilter ? 1 : 0);

  // ----- sortable header ----
  const headerSort = (field, label) => {
    const active = sortBy === field;
    return (
      <th
        className={`log-th-sortable${active ? " is-active" : ""}`}
        onClick={() => {
          if (active) setSortDir(sortDir === "asc" ? "desc" : "asc");
          else { setSortBy(field); setSortDir("desc"); }
        }}
        title={active
          ? `Sorted ${sortDir === "asc" ? "ascending" : "descending"} — click to flip`
          : `Sort by ${label}`}
      >
        {label}
        <i className={`fa ${active ? (sortDir === "asc" ? "fa-sort-asc" : "fa-sort-desc") : "fa-sort"}`}
           style={{ marginLeft: 6, opacity: active ? 1 : 0.4 }} aria-hidden />
      </th>
    );
  };

  // ----- actions ----
  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await api.seedAuditEvents({ count: 120 });
      showToast(`Seeded ${res.inserted} audit event(s)`, 2500, true, "success");
      await Promise.all([fetchEvents(1), fetchDistincts()]);
    } catch (err) {
      showToast(err.message || "Failed to seed logs", 3000, true, "danger");
    } finally {
      setSeeding(false);
    }
  };

  const handleClearAll = async () => {
    if (!clearPassword) return; // Also guarded by the disabled button.
    setClearing(true);
    try {
      const res = await api.clearAuditEvents(clearPassword);
      showToast(`Cleared ${res.cleared} event(s)`, 2500, true, "success");
      setClearConfirmOpen(false);
      setClearPassword("");
      await Promise.all([fetchEvents(1), fetchDistincts()]);
    } catch (err) {
      // Map the two well-known rejection shapes to actionable copy. Any
      // other failure surfaces the raw server message.
      if (err.status === 401) {
        showToast("Incorrect password. The audit log was not cleared.", 3500, true, "danger");
      } else if (err.status === 503) {
        showToast(
          err.data?.message || "Set AUDIT_CLEAR_PASSWORD in the backend .env first.",
          4500, true, "danger"
        );
      } else {
        showToast(err.message || "Failed to clear logs", 3000, true, "danger");
      }
    } finally {
      setClearing(false);
    }
  };

  // Reset the password field any time the modal opens or closes so it
  // never carries a stale value between attempts.
  useEffect(() => {
    if (!clearConfirmOpen) {
      setClearPassword("");
      setShowClearPassword(false);   // never reopen already revealed
    }
  }, [clearConfirmOpen]);

  // ---------------------------------------------------------------------------

  return (
    <div className="dashboard-container audit-log-page">
      <div className="dashboard-content">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Log</h1>
            <p className="dashboard-subtitle">
              Every user action — creations, updates, deletions, logins, transfers, theft alerts — in one place.
            </p>
          </div>
          <div className="log-actions">
            <button
              type="button"
              className="log-action-btn log-action-refresh"
              onClick={() => fetchEvents(page)}
              disabled={loading}
              title="Refresh"
            >
              <i className={`fa ${loading ? "fa-spinner fa-spin" : "fa-refresh"}`} aria-hidden />
              <span>Refresh</span>
            </button>
            <button
              type="button"
              className="log-action-btn log-action-seed"
              onClick={handleSeed}
              disabled={seeding}
              title="Insert 120 realistic demo events for testing"
            >
              <i className={`fa ${seeding ? "fa-spinner fa-spin" : "fa-flask"}`} aria-hidden />
              <span>{seeding ? "Seeding…" : "Seed Logs"}</span>
            </button>
            <button
              type="button"
              className="log-action-btn log-action-clear"
              onClick={() => setClearConfirmOpen(true)}
              disabled={clearing}
              title="Wipe the entire audit log"
            >
              <i className="fa fa-trash" aria-hidden />
              <span>Clear Log</span>
            </button>
          </div>
        </div>

        {/* ─────────── Filter bar ─────────── */}
        <div className="filters-section log-filters">
          {/* top row: free-text search + filter count + clear */}
          <div className="log-search-row">
            <div className="log-search-wrap">
              <i className="fa fa-search log-search-icon" aria-hidden />
              <input
                type="text"
                className="log-search-input"
                placeholder="Search actions, users, EPCs, descriptions…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  type="button"
                  className="log-search-clear"
                  onClick={() => setSearch("")}
                  title="Clear search"
                >
                  <i className="fa fa-times" aria-hidden />
                </button>
              )}
            </div>
            <div className="log-search-meta">
              {activeFilterCount > 0 && (
                <span className="log-active-filter-pill">
                  <i className="fa fa-filter" aria-hidden /> {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"}
                </span>
              )}
              <button
                type="button"
                className="log-mini-btn"
                onClick={clearFilters}
                disabled={activeFilterCount === 0}
              >
                <i className="fa fa-times" aria-hidden /> Clear filters
              </button>
            </div>
          </div>

          {/* chip rows — expiry events are hidden here; they live on /expiry-log. */}
          {distincts.types.filter((t) => !EXPIRY_EVENT_TYPES.includes(t)).length > 0 && (
            <div className="log-chip-row">
              <span className="log-chip-group-label">Event</span>
              <div className="log-chip-list">
                {distincts.types
                  .filter((t) => !EXPIRY_EVENT_TYPES.includes(t))
                  .map((t) => {
                    const meta = classifyType(t);
                    return (
                      <Chip
                        key={t}
                        active={selectedTypes.includes(t)}
                        onClick={() => toggleMulti(setSelectedTypes)(t)}
                        label={meta.label}
                        family={meta.family}
                        icon={meta.icon}
                      />
                    );
                  })}
              </div>
            </div>
          )}

          {distincts.thingTypes.length > 0 && (
            <div className="log-chip-row">
              <span className="log-chip-group-label">Scope</span>
              <div className="log-chip-list">
                {distincts.thingTypes.map((t) => (
                  <Chip
                    key={t}
                    active={selectedThingTypes.includes(t)}
                    onClick={() => toggleMulti(setSelectedThingTypes)(t)}
                    label={t || "—"}
                    family="other"
                  />
                ))}
              </div>
            </div>
          )}

          {distincts.roles.length > 0 && (
            <div className="log-chip-row">
              <span className="log-chip-group-label">Role</span>
              <div className="log-chip-list">
                {distincts.roles.map((r) => (
                  <Chip
                    key={r}
                    active={selectedRoles.includes(r)}
                    onClick={() => toggleMulti(setSelectedRoles)(r)}
                    label={r || "—"}
                    family="system"
                  />
                ))}
              </div>
            </div>
          )}

          {/* secondary inputs */}
          <div className="log-input-row">
            <div className="log-input-field">
              <label className="log-input-label">User</label>
              <input
                className="log-input"
                type="text"
                list="audit-user-list"
                placeholder="Filter by username…"
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
              />
              <datalist id="audit-user-list">
                {distincts.users.map((u) => <option key={u} value={u} />)}
              </datalist>
            </div>
            <div className="log-input-field">
              <label className="log-input-label">Tag ID / EPC</label>
              <input
                className="log-input"
                type="text"
                placeholder="TID or EPC contains…"
                value={tidFilter}
                onChange={(e) => setTidFilter(e.target.value)}
              />
            </div>
            <div className="log-input-field">
              <label className="log-input-label">From</label>
              <input
                className="log-input"
                type="datetime-local"
                value={fromFilter}
                onChange={(e) => setFromFilter(e.target.value)}
              />
            </div>
            <div className="log-input-field">
              <label className="log-input-label">To</label>
              <input
                className="log-input"
                type="datetime-local"
                value={toFilter}
                onChange={(e) => setToFilter(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ─────────── Results ─────────── */}
        <div className="table-section">
          <div className="section-header log-results-header">
            <h2 className="section-title">Events</h2>
            <div className="log-results-meta">
              <span className="results-count">
                {loading ? "Loading…" : `Showing ${events.length} of ${total.toLocaleString()}`}
              </span>
              <select
                className="log-page-size"
                value={pageSize}
                onChange={(e) => setPageSize(parseInt(e.target.value))}
                title="Page size"
              >
                {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n} / page</option>)}
              </select>
            </div>
          </div>

          <div className="table-container">
            {events.length === 0 && !loading ? (
              <div className="empty-state">
                <i className="fa fa-history empty-state-icon" />
                <p>No audit events match the current filters.</p>
              </div>
            ) : (
              <table className="tags-table audit-log-table">
                <thead>
                  <tr>
                    {headerSort("timestamp", "Time")}
                    {headerSort("type", "Event")}
                    {headerSort("user", "User")}
                    {headerSort("thingType", "Scope")}
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((evt) => {
                    const id = evt._id || `${evt.type}-${evt.timestamp}-${evt.thingId}`;
                    const meta = classifyType(evt.type);
                    const ts = evt.timestamp || evt.createdAt;
                    const target = tidTarget(evt);
                    return (
                      <tr
                        key={id}
                        className={`log-row log-row-${meta.family}${target ? " log-row-linked" : ""}`}
                        onClick={target ? () => navigate(target) : undefined}
                        title={target ? "Open tag details" : undefined}
                      >
                        <td className="log-when" title={formatExact(ts)}>
                          <span className="log-when-rel">{formatWhen(ts)}</span>
                          <span className="log-when-abs">{formatExact(ts)}</span>
                        </td>
                        <td>
                          <span className={`log-type-badge log-type-${meta.family}`}>
                            <i className={`fa ${meta.icon}`} aria-hidden /> {meta.label}
                          </span>
                        </td>
                        <td>
                          <div className="log-user-cell">
                            <span className="log-user-name">{evt.user || "—"}</span>
                            {evt.role && <span className={`log-role-pill log-role-${evt.role}`}>{evt.role}</span>}
                          </div>
                        </td>
                        <td>
                          {evt.thingType ? (
                            <span className="log-scope-pill">{evt.thingType}</span>
                          ) : <span style={{ color: "var(--text-tertiary)" }}>—</span>}
                        </td>
                        <td className="log-desc">{evt.description || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="audit-pagination">
            <button
              type="button"
              className="log-mini-btn"
              disabled={page <= 1 || loading}
              onClick={() => fetchEvents(page - 1)}
            >
              <i className="fa fa-chevron-left" aria-hidden /> Prev
            </button>
            <span className="audit-page-info">
              Page <strong>{page}</strong> of {totalPages}
            </span>
            <button
              type="button"
              className="log-mini-btn"
              disabled={page >= totalPages || loading}
              onClick={() => fetchEvents(page + 1)}
            >
              Next <i className="fa fa-chevron-right" aria-hidden />
            </button>
          </div>
        </div>
      </div>

      {/* Clear-all confirm — gated on a server-side password (AUDIT_CLEAR_PASSWORD).
          The button stays disabled until the admin types something, and the server
          still rejects the request if the value doesn't match. */}
      {clearConfirmOpen && (
        <div className="modal-overlay" onClick={() => !clearing && setClearConfirmOpen(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle" style={{ backgroundColor: "#FEE2E2", color: "#B91C1C" }}>
                <i className="fa fa-exclamation-triangle" />
              </div>
              <div className="modal-title-section">
                <span className="modal-title">Clear the audit log?</span>
                <span className="modal-subtitle">
                  This deletes every audit event permanently. A single "Log cleared" event will be written in their place.
                </span>
              </div>
            </div>
            <div className="modal-body-wrapper" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label className="log-input-label" htmlFor="audit-clear-password">
                Confirmation password
              </label>
              <div className="log-input-reveal">
                <input
                  id="audit-clear-password"
                  type={showClearPassword ? "text" : "password"}
                  className="log-input"
                  placeholder="Enter the clear-log password"
                  value={clearPassword}
                  onChange={(e) => setClearPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && clearPassword && !clearing) handleClearAll();
                  }}
                  autoFocus
                  autoComplete="new-password"
                  disabled={clearing}
                />
                <button
                  type="button"
                  className="log-input-reveal-btn"
                  onClick={() => setShowClearPassword((v) => !v)}
                  disabled={clearing}
                  aria-label={showClearPassword ? "Hide password" : "Show password"}
                  aria-pressed={showClearPassword}
                  title={showClearPassword ? "Hide password" : "Show password"}
                >
                  <i className={`fa ${showClearPassword ? "fa-eye-slash" : "fa-eye"}`} aria-hidden="true" />
                </button>
              </div>
              <p style={{ fontSize: 12, color: "var(--text-tertiary)", margin: 0 }}>
                Set by an admin in the backend <code>.env</code> as <code>AUDIT_CLEAR_PASSWORD</code>.
              </p>
            </div>
            <div className="modal-footer-wrapper">
              <button type="button" className="log-mini-btn" onClick={() => setClearConfirmOpen(false)} disabled={clearing}>Cancel</button>
              <button
                type="button"
                className="log-action-btn log-action-clear"
                onClick={handleClearAll}
                disabled={clearing || !clearPassword}
                title={clearPassword ? "Clear all audit events" : "Enter the password first"}
              >
                <i className={`fa ${clearing ? "fa-spinner fa-spin" : "fa-trash"}`} aria-hidden />
                <span>{clearing ? "Clearing…" : "Yes, clear all"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLog;
